import moment from "moment";
import {
  getHaklaiCurrencyOptions,
  normalizeHaklaiCurrencyForDate,
  ZABAN_ATTRIBUTION_OPTIONS
} from "../../shared/haklaiConstants";
import {
  getHaklaiLogicalPropertyPartKey,
  getHaklaiLogicalPropertyPartsData,
  getHaklaiPartDisplayLabel,
  getRenderablePropertyPartRows,
  hasPositiveNumericValue,
  hasRawNumericValue,
  isHaklaiZabanAttributionRelevant,
  mergeHaklaiPartKeyedDepreciationProfile
} from "../../shared/haklaiFlowHelpers";
import {
  calculateHaklaiAutoDepreciation,
  getHaklaiExpenseDepreciationMode,
  getHaklaiPropertyDepreciationValidationError,
  getHaklaiResidentialRentalProfile,
  hasHaklaiAutoDepreciationPeriodOverlap,
  HAKLAI_PROPERTY_DEPRECIATION_MODE,
  normalizeHaklaiDepreciationState
} from "../../shared/haklaiDepreciationHelpers";
import {
  getHaklaiExpenseDepreciationDecision,
  getHaklaiExpenseTypeDepreciationPolicy,
  hasHaklaiExpenseDepreciationDraftInput,
  hasHaklaiExpenseManualDepreciationInput,
  HAKLAI_EXPENSE_DEPRECIATION_DECISION,
  isHaklaiExpenseDepreciationPolicyRelevant,
  normalizeHaklaiExpenseDepreciationDecisionState
} from "./haklaiExpenseDepreciationPolicy";
import { HAKLAI_EXPENSE_ERRORS } from "../haklaiExpenseTexts";

// ─── Expense factory ─────────────────────────────────────────────────────────

export const DEFAULT_HAKLAI_ZABAN_ATTRIBUTION = "both";
export const DEFAULT_HAKLAI_EXPENSE_TYPE = "expenseGeneralImprovement";

// The closed סוג ניכוי cell shows the first two words of a value the table
// filled in itself. The dropdown list and the stored type keep the full text.
export const getHaklaiDefaultExpenseTypeTableLabel = (label) => {
  const words = String(label || "").trim().split(/\s+/).filter(Boolean);
  if (words.length <= 2) return String(label || "");
  return `${words.slice(0, 2).join(" ")}…`;
};
const HAKLAI_PART_EXPENSE_SCOPE = "part";

const getHaklaiHistoricalPropertyTaxDateError = (expenseType, expenseDate) => {
  const date = moment(expenseDate);
  if (!date.isValid()) return "";

  if (
    expenseType === "masRehushUpTo72April"
    && date.isAfter(moment("1972-03-31"), "day")
  ) {
    return HAKLAI_EXPENSE_ERRORS.masRehushUpTo72DateInvalid;
  }
  if (
    expenseType === "masRehushFrom72AprilTo86"
    && (
      date.isBefore(moment("1972-04-01"), "day")
      || date.isAfter(moment("1985-12-31"), "day")
    )
  ) {
    return HAKLAI_EXPENSE_ERRORS.masRehushFrom72To86DateInvalid;
  }
  if (
    expenseType === "masRehushFrom86"
    && date.isBefore(moment("1986-01-01"), "day")
  ) {
    return HAKLAI_EXPENSE_ERRORS.masRehushFrom86DateInvalid;
  }
  return "";
};

export const createHaklaiExpense = (
  defaultTargetPartKey = "",
  defaultZabanAttribution = "",
  defaultExpenseType = ""
) => ({
  id: `expense_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  expenseScope: HAKLAI_PART_EXPENSE_SCOPE,
  expenseType: defaultExpenseType,
  amount: null,
  expenseCurrency: "newShekel",
  expenseDate: "",
  targetPartKey: defaultTargetPartKey,
  zabanAttribution: defaultZabanAttribution,
  depreciationByPart: {}
});

// ─── Normalization ───────────────────────────────────────────────────────────

export const normalizeExpensesForState = (expenses) => {
  const rows = (Array.isArray(expenses) ? expenses : [])
    .filter((expense) => expense?.expenseScope !== "globalMeshek")
    .map((expense) => {
      const expenseWithoutDepreciation = { ...(expense || {}) };
      delete expenseWithoutDepreciation.isDepreciable;
      delete expenseWithoutDepreciation.depreciation;
      return {
        ...expenseWithoutDepreciation,
        id: expense?.id || `expense_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        expenseScope: getHaklaiExpenseScope(expense),
        expenseCurrency: normalizeHaklaiCurrencyForDate(
          expense?.expenseDate || "",
          expense?.expenseCurrency || "newShekel"
        ),
        targetPartKey: expense?.targetPartKey || "",
        zabanAttribution: typeof expense?.zabanAttribution === "string" ? expense.zabanAttribution : "",
        depreciationByPart: getExpenseDepreciationByPartForNormalization(expense)
      };
    });
  return rows;
};

export const removeExpensesFromState = (expenses, expenseIds = []) => {
  const rows = Array.isArray(expenses) ? expenses : [];
  const idsToRemove = Array.isArray(expenseIds) ? expenseIds.filter(Boolean) : [];
  if (!idsToRemove.length) return rows;
  const removeIdSet = new Set(idsToRemove);
  const filtered = rows.filter((expense) => !removeIdSet.has(expense.id));
  return filtered.length === rows.length ? rows : filtered;
};

// ─── Scope / target helpers ─────────────────────────────────────────────────

// Builds the dropdown options for "ייחוס הוצאה לחלק". Disambiguation of
// duplicate types is delegated to the shared `getHaklaiPartDisplayLabel`
// helper so the strings rendered here stay in lockstep with what the user
// sees in the property parts table itself.
const getSelectableExpenseTargetOptions = (propertyPartsData = {}, totalPropertyCost = null, hasZaban = false) => {
  const logicalPropertyPartsData = getHaklaiLogicalPropertyPartsData(propertyPartsData, totalPropertyCost, hasZaban);
  const allRows = getRenderablePropertyPartRows(logicalPropertyPartsData);
  // Group siblings per category once; the helper needs per-category context
  // to compute its (N) ordinal.
  const siblingsByCategory = allRows.reduce((acc, entry) => {
    const [, part] = entry;
    const category = part?.category;
    if (!category) return acc;
    if (!acc[category]) acc[category] = [];
    acc[category].push(entry);
    return acc;
  }, {});

  return allRows.flatMap((entry) => {
    const [key, part] = entry;
    const label = getHaklaiPartDisplayLabel(entry, siblingsByCategory[part?.category] || []);
    if (!label) return [];
    return [{ value: key, label }];
  });
};

const getHaklaiExpenseScope = () => HAKLAI_PART_EXPENSE_SCOPE;

export const normalizeHaklaiExpenseDepreciationByPart = (
  depreciationByPart = {},
  allowedPartKeys = null,
  propertyPartsData = {},
  expenseType = ""
) => {
  if (!depreciationByPart || typeof depreciationByPart !== "object" || Array.isArray(depreciationByPart)) {
    return {};
  }
  const allowedKeySet = Array.isArray(allowedPartKeys) ? new Set(allowedPartKeys) : null;
  const hasPropertyPartsContext = Boolean(Object.keys(propertyPartsData || {}).length);
  const policy = getHaklaiExpenseTypeDepreciationPolicy(expenseType);
  if (!isHaklaiExpenseDepreciationPolicyRelevant(policy)) return {};

  return Object.entries(depreciationByPart).reduce((acc, [partKey, depreciation]) => {
    if (!partKey || (allowedKeySet && !allowedKeySet.has(partKey))) return acc;
    if (!hasHaklaiExpenseDepreciationDraftInput(depreciation)) return acc;
    const decision = getHaklaiExpenseDepreciationDecision(depreciation);
    const mode = getHaklaiExpenseDepreciationMode(propertyPartsData[partKey]);
    const isResidentialAutoDecision = decision === HAKLAI_EXPENSE_DEPRECIATION_DECISION.RESIDENTIAL_AUTO;
    const isManualForm11Decision = decision === HAKLAI_EXPENSE_DEPRECIATION_DECISION.FORM_11;
    const isDisabledManualForm11Decision = (
      decision === HAKLAI_EXPENSE_DEPRECIATION_DECISION.NONE
      && hasHaklaiExpenseManualDepreciationInput(depreciation)
    );
    const isResidentialAutoCompatible = (
      isResidentialAutoDecision
      && (!hasPropertyPartsContext || mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO)
    );
    const isManualForm11Compatible = (
      isManualForm11Decision
      && (!hasPropertyPartsContext || mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11)
    );
    const isDisabledManualForm11Compatible = (
      isDisabledManualForm11Decision
      && (!hasPropertyPartsContext || mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11)
    );
    if (
      !isResidentialAutoCompatible
      && !isManualForm11Compatible
      && !isDisabledManualForm11Compatible
    ) {
      return acc;
    }
    const normalizedDepreciation = normalizeHaklaiExpenseDepreciationDecisionState(
      normalizeHaklaiDepreciationState(depreciation)
    );
    if (Object.keys(normalizedDepreciation).length > 0) {
      acc[partKey] = normalizedDepreciation;
    }
    return acc;
  }, {});
};

const hasExpenseDepreciationByPartChanged = (current = {}, next = {}) => (
  JSON.stringify(current || {}) !== JSON.stringify(next || {})
);

const getExpenseDepreciationByPartForNormalization = (expense = {}) => {
  const normalizedDepreciationByPart = normalizeHaklaiExpenseDepreciationByPart(
    expense?.depreciationByPart,
    null,
    {},
    expense?.expenseType || ""
  );
  if (Object.keys(normalizedDepreciationByPart).length > 0) return normalizedDepreciationByPart;
  return {};
};

const remapExpenseDepreciationByPartToLogicalKeys = (
  depreciationByPart = {},
  propertyPartsData = {},
  logicalPropertyPartsData = {}
) => {
  const entries = (
    depreciationByPart
    && typeof depreciationByPart === "object"
    && !Array.isArray(depreciationByPart)
  ) ? Object.entries(depreciationByPart) : [];

  return entries.reduce((acc, [partKey, depreciation]) => {
    const logicalPartKey = getHaklaiLogicalPropertyPartKey({
      partKey,
      propertyPartsData,
      logicalPropertyPartsData
    });
    if (!logicalPartKey) return acc;
    acc[logicalPartKey] = mergeHaklaiPartKeyedDepreciationProfile(acc[logicalPartKey], depreciation);
    return acc;
  }, {});
};

export const getHaklaiExpenseAllocations = ({
  expense = {},
  propertyPartsData = {}
}) => {
  const expenseAmount = Number(expense?.amount);
  const hasPositiveExpenseAmount = Number.isFinite(expenseAmount) && expenseAmount > 0;

  if (!expense?.targetPartKey || !propertyPartsData[expense.targetPartKey]) return [];
  return [{
    partKey: expense.targetPartKey,
    part: propertyPartsData[expense.targetPartKey],
    amount: hasPositiveExpenseAmount ? expenseAmount : 0
  }];
};

const isExpenseDateReadyForDepreciation = ({
  expenseDate = "",
  soldPropertyDate = "",
  ownershipStartDate = ""
} = {}) => {
  if (!expenseDate) return false;
  const parsedExpenseDate = moment(expenseDate);
  if (!parsedExpenseDate.isValid()) return false;
  if (parsedExpenseDate.isAfter(moment(), "day")) return false;
  if (
    ownershipStartDate
    && moment(ownershipStartDate).isValid()
    && parsedExpenseDate.isBefore(moment(ownershipStartDate), "day")
  ) {
    return false;
  }
  if (
    soldPropertyDate
    && moment(soldPropertyDate).isValid()
    && parsedExpenseDate.isAfter(moment(soldPropertyDate), "day")
  ) {
    return false;
  }
  return true;
};

export const normalizeHaklaiExpensesForDependencies = (
  expenses = [],
  propertyPartsData = {},
  hasZaban = false,
  totalPropertyCost = null
) => {
  const logicalPropertyPartsData = getHaklaiLogicalPropertyPartsData(propertyPartsData, totalPropertyCost, hasZaban);
  const selectableTargetKeySet = new Set(
    getSelectableExpenseTargetOptions(propertyPartsData, totalPropertyCost, hasZaban).map((option) => option.value)
  );
  let changed = false;
  const normalizedExpenses = (Array.isArray(expenses) ? expenses : []).reduce((acc, expense) => {
    const expenseWithoutDepreciation = { ...(expense || {}) };
    delete expenseWithoutDepreciation.isDepreciable;
    delete expenseWithoutDepreciation.depreciation;
    const hasLegacyDepreciationFields = Boolean(
      Object.prototype.hasOwnProperty.call(expense || {}, "isDepreciable")
      || Object.prototype.hasOwnProperty.call(expense || {}, "depreciation")
    );
    const nextExpenseBase = {
      ...expenseWithoutDepreciation
    };
    const expenseScope = getHaklaiExpenseScope(expense);
    const resolvedTarget = getHaklaiLogicalPropertyPartKey({
      partKey: expense?.targetPartKey,
      propertyPartsData,
      logicalPropertyPartsData
    });
    const nextTarget = selectableTargetKeySet.has(resolvedTarget) ? resolvedTarget : "";

    if (!nextTarget) {
      changed = true;
      return acc;
    }

    const currentZaban = typeof expense?.zabanAttribution === "string" ? expense.zabanAttribution : "";
    const isZabanRelevant = isHaklaiZabanAttributionRelevant(logicalPropertyPartsData, nextTarget, hasZaban);
    const nextZaban = isZabanRelevant ? (currentZaban || DEFAULT_HAKLAI_ZABAN_ATTRIBUTION) : "";
    const allocationExpense = { ...expense, expenseScope, targetPartKey: nextTarget };
    const allocationPartKeys = getHaklaiExpenseAllocations({
      expense: allocationExpense,
      propertyPartsData: logicalPropertyPartsData
    }).map((allocation) => allocation.partKey);
    const remappedDepreciationByPart = remapExpenseDepreciationByPartToLogicalKeys(
      getExpenseDepreciationByPartForNormalization(expense),
      propertyPartsData,
      logicalPropertyPartsData
    );
    const nextDepreciationByPart = normalizeHaklaiExpenseDepreciationByPart(
      remappedDepreciationByPart,
      allocationPartKeys,
      logicalPropertyPartsData,
      expense?.expenseType || ""
    );
    const currentRawDepreciationByPart = (
      expense?.depreciationByPart
      && typeof expense.depreciationByPart === "object"
      && !Array.isArray(expense.depreciationByPart)
    ) ? expense.depreciationByPart : {};
    const currentDepreciationByPart = getExpenseDepreciationByPartForNormalization(expense);
    const nextExpenseWithDepreciation = Object.keys(nextDepreciationByPart).length > 0
      ? { ...nextExpenseBase, depreciationByPart: nextDepreciationByPart }
      : { ...nextExpenseBase, depreciationByPart: {} };
    const depreciationChanged = hasExpenseDepreciationByPartChanged(
      currentDepreciationByPart,
      nextDepreciationByPart
    ) || hasExpenseDepreciationByPartChanged(
      currentRawDepreciationByPart,
      nextDepreciationByPart
    );

    if (
      expenseScope !== expense?.expenseScope
      || nextTarget !== expense?.targetPartKey
      || nextZaban !== currentZaban
      || hasLegacyDepreciationFields
      || depreciationChanged
    ) {
      changed = true;
      acc.push({ ...nextExpenseWithDepreciation, expenseScope, targetPartKey: nextTarget, zabanAttribution: nextZaban });
      return acc;
    }

    if (typeof expense?.zabanAttribution !== "string") {
      changed = true;
      acc.push({ ...nextExpenseWithDepreciation, expenseScope, targetPartKey: nextTarget, zabanAttribution: "" });
      return acc;
    }

    acc.push(expenseScope === expense?.expenseScope ? nextExpenseWithDepreciation : { ...nextExpenseWithDepreciation, expenseScope });
    return acc;
  }, []);

  return { changed, expenses: normalizedExpenses };
};

export const getHaklaiExpenseDepreciationSummary = ({
  expense = {},
  propertyPartsData = {},
  residentialRentalByPart = {},
  purchaseDepreciationByPart = {},
  soldPropertyDate = "",
  ownershipStartDate = ""
}) => {
  const allocations = getHaklaiExpenseAllocations({ expense, propertyPartsData });
  const expensePolicy = getHaklaiExpenseTypeDepreciationPolicy(expense?.expenseType || "");
  const isExpensePolicyRelevant = isHaklaiExpenseDepreciationPolicyRelevant(expensePolicy);
  const isExpenseDateReady = isExpenseDateReadyForDepreciation({
    expenseDate: expense?.expenseDate || "",
    soldPropertyDate,
    ownershipStartDate
  });
  const depreciationByPart = normalizeHaklaiExpenseDepreciationByPart(
    getExpenseDepreciationByPartForNormalization(expense),
    null,
    propertyPartsData,
    expense?.expenseType || ""
  );
  let totalDepreciationResult = 0;
  let totalAvailableDepreciationResult = 0;
  let hasConfiguredDepreciation = false;
  let hasCalculatedDepreciation = false;
  let hasAvailableDepreciationResult = false;
  let hasInvalidDepreciation = false;

  const allocationSummaries = allocations.map((allocation) => {
    const part = allocation.part || {};
    const mode = getHaklaiExpenseDepreciationMode(part);
    const isResidentialAutoDepreciationRelevant = (
      isExpensePolicyRelevant
      && mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
    );
    const isSelectionOnlyDepreciationRelevant = (
      isExpensePolicyRelevant
      && mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11
    );
    const isRelevant = isResidentialAutoDepreciationRelevant || isSelectionOnlyDepreciationRelevant;
    const depreciation = normalizeHaklaiDepreciationState(depreciationByPart[allocation.partKey]);
    const decision = getHaklaiExpenseDepreciationDecision(depreciation);
    const hasDecision = Boolean(decision);
    const hasResidentialAutoDepreciation = (
      decision === HAKLAI_EXPENSE_DEPRECIATION_DECISION.RESIDENTIAL_AUTO
    );
    const hasSelectionOnlyDepreciation = decision === HAKLAI_EXPENSE_DEPRECIATION_DECISION.FORM_11;
    const residentialRentalProfile = getHaklaiResidentialRentalProfile({
      partKey: allocation.partKey,
      part,
      residentialRentalByPart,
      purchaseDepreciationByPart
    });
    const hasResidentialRentalProfile = Object.keys(residentialRentalProfile || {}).length > 0;
    const hasRequiredDepreciationProfile = (
      (hasResidentialAutoDepreciation && hasResidentialRentalProfile)
      || hasSelectionOnlyDepreciation
    );
    const hasDepreciation = isRelevant
      && hasRequiredDepreciationProfile
      && (
        hasResidentialAutoDepreciation
        || hasSelectionOnlyDepreciation
      );
    let validationError = "";
    let depreciationResult = null;
    let availableDepreciationValidationError = "";
    let availableDepreciationResult = null;
    if (
      isResidentialAutoDepreciationRelevant
      && hasResidentialRentalProfile
      && isExpenseDateReady
    ) {
      availableDepreciationValidationError = getHaklaiPropertyDepreciationValidationError({
        part,
        depreciation: residentialRentalProfile,
        soldPropertyDate,
        ownershipStartDate,
        modeOverride: HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
      });
      if (!availableDepreciationValidationError) {
        availableDepreciationResult = calculateHaklaiAutoDepreciation({
          amount: allocation.amount,
          part,
          depreciation: residentialRentalProfile,
          acquisitionAmount: allocation.amount,
          clampStartDate: expense?.expenseDate || "",
          clampEndDate: soldPropertyDate || ""
        });
        if (!Number.isFinite(availableDepreciationResult) || availableDepreciationResult < 0) {
          availableDepreciationValidationError = HAKLAI_EXPENSE_ERRORS.depreciationDmmInvalid;
          availableDepreciationResult = null;
        }
      }
    }
    const hasNoEligibleResidentialDepreciationPeriod = (
      isResidentialAutoDepreciationRelevant
      && hasResidentialRentalProfile
      && isExpenseDateReady
      && !availableDepreciationValidationError
      && !hasHaklaiAutoDepreciationPeriodOverlap({
        depreciation: residentialRentalProfile,
        clampStartDate: expense?.expenseDate || "",
        clampEndDate: soldPropertyDate || ""
      })
    );

    if (hasResidentialAutoDepreciation && hasResidentialRentalProfile) {
      validationError = availableDepreciationValidationError;
      depreciationResult = availableDepreciationResult;
    } else if (hasSelectionOnlyDepreciation) {
      const manualDepreciationResult = Number(depreciation.depreciationResult);
      if (!Number.isFinite(manualDepreciationResult) || manualDepreciationResult <= 0) {
        validationError = HAKLAI_EXPENSE_ERRORS.form11DepreciationAmountRequired;
      } else {
        depreciationResult = manualDepreciationResult;
      }
    }

    if (hasDepreciation) hasConfiguredDepreciation = true;
    if (validationError) hasInvalidDepreciation = true;
    if (Number.isFinite(depreciationResult)) {
      hasCalculatedDepreciation = true;
      totalDepreciationResult += depreciationResult;
    }
    if (Number.isFinite(availableDepreciationResult)) {
      hasAvailableDepreciationResult = true;
      totalAvailableDepreciationResult += availableDepreciationResult;
    }

    return {
      ...allocation,
      mode,
      policy: expensePolicy,
      isRelevant,
      isResidentialAutoDepreciationRelevant,
      isSelectionOnlyDepreciationRelevant,
      decision,
      hasDecision,
      depreciation,
      hasDepreciation,
      hasRequiredDepreciationProfile,
      hasResidentialAutoDepreciation,
      hasSelectionOnlyDepreciation,
      hasManualDepreciation: false,
      hasNoDepreciationDecision: false,
      validationError,
      depreciationResult,
      availableDepreciationResult,
      availableDepreciationValidationError,
      hasNoEligibleResidentialDepreciationPeriod,
      depreciationCurrency: hasSelectionOnlyDepreciation
        ? (depreciation.depCurrency || "newShekel")
        : (expense?.expenseCurrency || "newShekel")
    };
  });
  const hasManualForm11Depreciation = allocationSummaries.some((allocation) => (
    allocation.hasSelectionOnlyDepreciation
  ));

  return {
    allocations: allocationSummaries,
    isRelevant: allocationSummaries.some((allocation) => allocation.isRelevant),
    policy: expensePolicy,
    isDecisionRequired: false,
    hasMissingRequiredDecision: false,
    hasManualDepreciation: false,
    hasNoDepreciationDecision: false,
    hasConfiguredDepreciation,
    hasCalculatedDepreciation,
    hasInvalidDepreciation,
    configuredCount: allocationSummaries.filter((allocation) => allocation.hasDepreciation).length,
    totalDepreciationResult: hasCalculatedDepreciation && !hasInvalidDepreciation
      ? parseFloat(totalDepreciationResult.toFixed(2))
      : null,
    totalAvailableDepreciationResult: hasAvailableDepreciationResult
      ? parseFloat(totalAvailableDepreciationResult.toFixed(2))
      : null,
    displayCurrency: hasManualForm11Depreciation
      ? "newShekel"
      : (expense?.expenseCurrency || "newShekel")
  };
};

// ─── Row-level validation ────────────────────────────────────────────────────

export const buildHaklaiExpenseValidationContext = (
  propertyPartsData = {},
  totalPropertyCost = null,
  hasZaban = false
) => ({
  selectableTargetKeySet: new Set(
    getSelectableExpenseTargetOptions(propertyPartsData, totalPropertyCost, hasZaban).map((option) => option.value)
  ),
  zabanAttributionValues: new Set(ZABAN_ATTRIBUTION_OPTIONS.map((option) => option.value))
});

export const isHaklaiExpenseStarted = (expense = {}) => {
  const hasType = Boolean(String(expense?.expenseType || "").trim());
  const hasAmount = hasRawNumericValue(expense?.amount);
  const hasDate = Boolean(expense?.expenseDate);
  const hasDepreciationData = Object.keys(normalizeHaklaiExpenseDepreciationByPart(
    expense?.depreciationByPart,
    null,
    {},
    expense?.expenseType || ""
  )).length > 0;
  return hasType || hasAmount || hasDate || hasDepreciationData;
};

export const getHaklaiExpenseValidationState = ({
  expense = {},
  soldPropertyDate = "",
  ownershipStartDate = "",
  propertyPartsData = {},
  hasZaban = false,
  validationContext = null
}) => {
  const expenseScope = getHaklaiExpenseScope(expense);
  const selectableTargetKeySet = validationContext?.selectableTargetKeySet || new Set(
    getSelectableExpenseTargetOptions(propertyPartsData, null, hasZaban).map((option) => option.value)
  );
  const zabanAttributionValues = validationContext?.zabanAttributionValues || new Set(
    ZABAN_ATTRIBUTION_OPTIONS.map((option) => option.value)
  );
  const fieldErrors = {};
  const immediateInvalidFields = [];
  const missingRequiredFields = [];
  const trimmedExpenseType = String(expense?.expenseType || "").trim();

  if (!trimmedExpenseType) {
    fieldErrors.expenseType = HAKLAI_EXPENSE_ERRORS.expenseTypeRequired;
    missingRequiredFields.push("expenseType");
  }

  if (!expense?.expenseDate) {
    fieldErrors.expenseDate = HAKLAI_EXPENSE_ERRORS.expenseDateRequired;
    missingRequiredFields.push("expenseDate");
  } else {
    const expenseDate = moment(expense.expenseDate);
    if (!expenseDate.isValid()) {
      fieldErrors.expenseDate = HAKLAI_EXPENSE_ERRORS.expenseDateInvalid;
      immediateInvalidFields.push("expenseDate");
    } else if (expenseDate.isAfter(moment(), "day")) {
      fieldErrors.expenseDate = HAKLAI_EXPENSE_ERRORS.expenseDateFuture;
      immediateInvalidFields.push("expenseDate");
    } else if (ownershipStartDate && moment(ownershipStartDate).isValid() && expenseDate.isBefore(moment(ownershipStartDate), "day")) {
      fieldErrors.expenseDate = HAKLAI_EXPENSE_ERRORS.expenseDateBeforeOwnershipStart;
      immediateInvalidFields.push("expenseDate");
    } else if (soldPropertyDate && expenseDate.isAfter(moment(soldPropertyDate), "day")) {
      fieldErrors.expenseDate = HAKLAI_EXPENSE_ERRORS.expenseDateAfterSale;
      immediateInvalidFields.push("expenseDate");
    }
  }
  if (!fieldErrors.expenseDate) {
    const historicalPropertyTaxDateError = getHaklaiHistoricalPropertyTaxDateError(
      trimmedExpenseType,
      expense.expenseDate
    );
    if (historicalPropertyTaxDateError) {
      fieldErrors.expenseDate = historicalPropertyTaxDateError;
      immediateInvalidFields.push("expenseDate");
    }
  }

  if (!hasPositiveNumericValue(expense?.amount)) {
    fieldErrors.amount = HAKLAI_EXPENSE_ERRORS.amountRequired;
    missingRequiredFields.push("amount");
  }

  const allowedCurrencyValues = new Set(
    getHaklaiCurrencyOptions(expense?.expenseDate || "").map((option) => option.value)
  );
  if (!expense?.expenseCurrency) {
    fieldErrors.expenseCurrency = HAKLAI_EXPENSE_ERRORS.currencyRequired;
    missingRequiredFields.push("expenseCurrency");
  } else if (!allowedCurrencyValues.has(expense.expenseCurrency)) {
    fieldErrors.expenseCurrency = HAKLAI_EXPENSE_ERRORS.currencyInvalidForDate;
    immediateInvalidFields.push("expenseCurrency");
  }

  if (!expense?.targetPartKey) {
    fieldErrors.targetPartKey = HAKLAI_EXPENSE_ERRORS.targetPartRequired;
    missingRequiredFields.push("targetPartKey");
  } else if (!propertyPartsData[expense.targetPartKey] || !selectableTargetKeySet.has(expense.targetPartKey)) {
    fieldErrors.targetPartKey = HAKLAI_EXPENSE_ERRORS.targetPartInvalid;
    immediateInvalidFields.push("targetPartKey");
  }

  const isZabanRelevant = isHaklaiZabanAttributionRelevant(propertyPartsData, expense?.targetPartKey, hasZaban);
  if (isZabanRelevant) {
    if (!expense?.zabanAttribution) {
      fieldErrors.zabanAttribution = HAKLAI_EXPENSE_ERRORS.zabanAttributionRequired;
      missingRequiredFields.push("zabanAttribution");
    } else if (!zabanAttributionValues.has(expense.zabanAttribution)) {
      fieldErrors.zabanAttribution = HAKLAI_EXPENSE_ERRORS.zabanAttributionInvalid;
      immediateInvalidFields.push("zabanAttribution");
    }
  }

  const invalidFields = ["expenseType", "expenseDate", "amount", "expenseCurrency", "targetPartKey", "zabanAttribution"]
    .filter((f) => Boolean(fieldErrors[f]));
  const firstInvalidField = [
    ...immediateInvalidFields,
    ...missingRequiredFields.filter((f) => !immediateInvalidFields.includes(f))
  ][0] || "";

  return {
    expenseScope,
    isReusableDraft: !isHaklaiExpenseStarted(expense),
    isStarted: isHaklaiExpenseStarted(expense),
    isZabanRelevant,
    isReady: invalidFields.length === 0,
    firstInvalidField,
    invalidFields,
    immediateInvalidFields,
    missingRequiredFields,
    fieldErrors
  };
};

// ─── Validation overlay (per-cell error messages) ───────────────────────────

// Drives the row-level validation UI for the expenses table.
// `cellErrors[expenseId][fieldName] = message` feeds the per-input red "!"
// indicator and ARIA error text.
//
// Render order mirrors the section's actual rendering: for each asset group
// (in the order produced by `getHaklaiAssetGroups`), validate the rows in
// `expensesByPartKey[assetGroup.partKey]`.
//
// Gating rules (kept here so expenses stay symmetric with property parts):
//   - Skip entirely when the row is a reusable draft *and* there has been no
//     interaction (neither committed nor any touched field). Commit or any
//     touch revives the row.
//   - Within a surfaced row, reveal a given invalid field only if the error
//     is "immediate" (`immediateInvalidFields`), the row is committed, or
//     that specific field has been touched.
export const buildHaklaiExpensesValidationOverlay = ({
  assetGroups = [],
  expensesByPartKey = {},
  touchedFieldsByRow = {},
  committedRows = {},
  soldPropertyDate = "",
  ownershipStartDate = "",
  propertyPartsData = {},
  hasZaban = false,
  validationStateById = null,
  validationContext = null
} = {}) => {
  const resolvedAssetGroups = Array.isArray(assetGroups) ? assetGroups : [];

  const buckets = [];
  resolvedAssetGroups.forEach((group) => {
    buckets.push(expensesByPartKey?.[group?.partKey] || []);
  });

  const cellErrors = {};

  buckets.forEach((bucketRows) => {
    const rows = Array.isArray(bucketRows) ? bucketRows : [];

    rows.forEach((expense) => {
      const expenseId = expense?.id;
      if (!expenseId) return;

      const validationState = validationStateById?.[expenseId] || getHaklaiExpenseValidationState({
        expense,
        soldPropertyDate,
        ownershipStartDate,
        propertyPartsData,
        hasZaban,
        validationContext
      });

      const rowTouched = touchedFieldsByRow?.[expenseId] || {};
      const isCommitted = Boolean(committedRows?.[expenseId]);
      const hasRowInteraction = isCommitted || Object.keys(rowTouched).length > 0;

      if (validationState.isReusableDraft && !hasRowInteraction) return;

      const rowCellErrors = {};

      validationState.invalidFields.forEach((fieldName) => {
        const message = validationState.fieldErrors[fieldName];
        if (!message) return;
        const isImmediate = validationState.immediateInvalidFields.includes(fieldName);
        const isTouched = Boolean(rowTouched[fieldName]);
        if (!isImmediate && !isCommitted && !isTouched) return;
        if (rowCellErrors[fieldName]) return;
        rowCellErrors[fieldName] = message;
      });

      if (Object.keys(rowCellErrors).length > 0) {
        cellErrors[expenseId] = rowCellErrors;
      }
    });
  });

  return { cellErrors };
};
